import { isSignedIn, loadSession, endSession, login, sessionExpiresAt, calendarReady } from './auth.js';

/**
 * Keeps the app signed in only while the CRM session lives.
 *
 * Every few minutes, while a team member has the app open, the CRM is asked silently (a hidden
 * frame to /auth/silent: the CRM answers at once, without a login page). The answers:
 *
 *   ok               the member is still allowed — the server renews the app session
 *   login_required   the member signed out of the CRM
 *   access_denied    the account is no longer allowed
 *
 * The last two end the app session — but never a call in progress: a warning is shown, and the
 * session ends when the call does (callEnded()). If the frame cannot be answered (the CRM
 * forbids framing, say — the app remembers this on the device), it asks in the page itself instead, as
 * a quick bounce through the CRM: when the session is about to lapse, or the server holds no CRM token
 * for the member yet, and nothing is in progress. A call is never interrupted by either.
 *
 * @param {object} options
 * @param {() => boolean} options.inCall
 * @param {(status: string) => void} options.onWarning  the session has ended; a call is in progress
 * @param {(status: string) => void} options.onEnded    the session ended and nothing is in progress
 */
const NOFRAME = 'trtmeet.noframe';
const BOUNCE_AT = 'trtmeet.silent-at';
const BOUNCE_EVERY_MS = 90_000;

export function startSessionWatch({
  inCall,
  onWarning,
  onEnded,
  intervalMs = 4 * 60_000,
  firstCheckMs = 30_000,
  frameTimeoutMs = 10_000,
  lapseSoonMs = 6 * 60_000,
  win = typeof window !== 'undefined' ? window : null,
  doc = typeof document !== 'undefined' ? document : null,
  now = () => Date.now(),
} = {}) {
  if (!win || !doc) return { stop() {}, callEnded() {}, check: async () => 'unavailable' };

  let timer = null;
  let first = null;
  let checking = null;
  let lastCheck = 0;
  let frameFailures = 0;
  let pendingEnd = null; // a status waiting for the call to finish
  let stopped = false;

  const flag = (key, value) => {
    try {
      if (value === undefined) return win.localStorage?.getItem(key) ?? null;
      win.localStorage?.setItem(key, value);
    } catch {
      /* private mode: forget */
    }
    return null;
  };
  const guard = (key, value) => {
    try {
      if (value === undefined) return win.sessionStorage?.getItem(key) ?? null;
      win.sessionStorage?.setItem(key, value);
    } catch {
      /* ignore */
    }
    return null;
  };

  /**
   * The CRM cannot be asked in a frame here: renew in the page itself, but only when it matters
   * (session about to lapse, or no CRM token held for the member) and nothing is in progress. At
   * most one bounce per minute and a half, so a CRM that refuses can never trap the page in a loop.
   */
  function renewInPage() {
    if (inCall()) return 'in-call';
    const expires = sessionExpiresAt();
    const needed = !calendarReady() || (expires && expires - now() < lapseSoonMs);
    if (!needed) return 'fresh';
    if (now() - Number(guard(BOUNCE_AT) ?? 0) < BOUNCE_EVERY_MS) return 'waiting';
    guard(BOUNCE_AT, String(now()));
    login({ silent: true, win });
    return 'redirected';
  }

  /** Ask the CRM in a hidden frame. Resolves one of the statuses above, or 'timeout'. */
  function ask() {
    return new Promise((resolve) => {
      const frame = doc.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.title = '';
      frame.style.display = 'none';
      let settled = false;
      const done = (status) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        win.removeEventListener('message', onMessage);
        frame.remove();
        resolve(status);
      };
      const onMessage = (event) => {
        if (event.origin !== win.location.origin || event.source !== frame.contentWindow) return;
        if (event.data?.type === 'trtmeet:silent' && typeof event.data.status === 'string') done(event.data.status);
      };
      const timeout = setTimeout(() => done('timeout'), frameTimeoutMs);
      win.addEventListener('message', onMessage);
      frame.src = '/auth/silent';
      doc.body.append(frame);
    });
  }

  function ended(status) {
    if (inCall()) {
      if (!pendingEnd) onWarning(status);
      pendingEnd = status;
      return;
    }
    endSession();
    onEnded(status);
  }

  async function check() {
    if (stopped || !isSignedIn()) return 'signed-out';
    if (checking) return checking;
    checking = (async () => {
      if (flag(NOFRAME)) {
        lastCheck = now();
        return renewInPage();
      }
      const status = await ask();
      lastCheck = now();
      if (status === 'timeout') flag(NOFRAME, '1');
      if (status === 'ok') {
        frameFailures = 0;
        await loadSession();
      } else if (status === 'login_required' || status === 'access_denied') {
        ended(status);
      } else {
        frameFailures += 1;
        const expires = sessionExpiresAt();
        if (frameFailures >= 2 && !inCall() && expires && expires - now() < lapseSoonMs) login({ silent: true, win });
      }
      return status;
    })().finally(() => {
      checking = null;
    });
    return checking;
  }

  const onVisible = () => {
    if (doc.visibilityState === 'visible' && now() - lastCheck > intervalMs / 2) void check();
  };
  // Soon, when the server holds no CRM token for the member yet (after a restart): the check renews it.
  first = setTimeout(() => void check(), isSignedIn() && !calendarReady() ? Math.min(firstCheckMs, 1500) : firstCheckMs);
  timer = setInterval(() => void check(), intervalMs);
  doc.addEventListener('visibilitychange', onVisible);

  return {
    check,
    /** The call is over: a session that ended during it ends now. */
    callEnded() {
      if (!pendingEnd) return;
      const status = pendingEnd;
      pendingEnd = null;
      endSession();
      onEnded(status);
    },
    stop() {
      stopped = true;
      clearTimeout(first);
      clearInterval(timer);
      doc.removeEventListener('visibilitychange', onVisible);
    },
  };
}
